"use client";

import HighlightTitle from "@/components/HighlightTitle";
import Page from "@/components/Page";
import Section from "@/components/ui/Section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { textKey } from "@/lib/list-keys";
import { tinaField, useTina } from "@/lib/utils";
import { Check } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

export default function PricingPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const content = data.pricing;
  const fallback = props.fallbackData?.data?.pricing;

  const mergedContent = {
    ...content,
    annualSavingsPercentage: content.annualSavingsPercentage ?? fallback?.annualSavingsPercentage,
    trialLabel: content.trialLabel ?? fallback?.trialLabel,
    pricingPlans: content.pricingPlans?.map((plan, index) => ({
      ...plan,
      monthlyPricing: plan.monthlyPricing ?? fallback?.pricingPlans?.[index]?.monthlyPricing,
      annualPricing: plan.annualPricing ?? fallback?.pricingPlans?.[index]?.annualPricing,
    })),
  };

  return (
    <Page defaultTinaField={data.pricing}>
      <Header tinaFieldData={content} {...content} />
      <PricingPlans tinaFieldData={content} {...mergedContent} />
    </Page>
  );
}

function Header({ tinaFieldData, title, subtitle }) {
  return (
    <Section as="header" wrapperClassName="bg-muted" backgroundPattern={false}>
      <div className="flex flex-col items-center gap-12 relative p-6 lg:p-12">
        <div className="absolute w-16 h-10 border-[3px_0_0px_3px] border-primary top-0 left-0" />
        <div className="absolute w-16 h-10 border-[0px_3px_3px_0px] border-primary bottom-0 right-0" />
        <div className="flex flex-col text-center items-center max-w-[840px]">
          <HighlightTitle as="h1" className="h0" content={title} data-tina-field={tinaField(tinaFieldData, "title")} />
          <p data-tina-field={tinaField(tinaFieldData, "subtitle")} className="p-lg whitespace-pre-line mt-6">
            {subtitle}
          </p>
        </div>
      </div>
    </Section>
  );
}

function PricingPlans({ tinaFieldData, pricingPlans, annualSavingsPercentage, trialLabel }) {
  const [billingPeriod, setBillingPeriod] = useState("annual");

  return (
    <Section wrapperClassName="!pt-12 pb-12">
      <div className="flex flex-col items-center mb-12">
        <Tabs value={billingPeriod} onValueChange={setBillingPeriod}>
          <TabsList>
            <TabsTrigger value="monthly" className="min-w-[100px] md:min-w-[150px]">
              Monthly
            </TabsTrigger>
            <TabsTrigger value="annual" className="gap-3 min-w-[100px] md:min-w-[150px]">
              Annual
              {annualSavingsPercentage && (
                <Badge variant="primary" className="text-[10px] px-2 py-0.5" data-tina-field={tinaField(tinaFieldData, "annualSavingsPercentage")}>
                  Save {annualSavingsPercentage}%
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center mb-16">
        {pricingPlans?.map((plan, index) => {
          const pricing = billingPeriod === "monthly" ? plan.monthlyPricing : plan.annualPricing;

          return (
            <Card key={textKey(plan.title, index)} className="border-primary-250/50 h-full">
              <CardContent className="h-full flex flex-col gap-2 relative">
                {trialLabel && (
                  <Badge
                    variant="secondary"
                    className="w-fit !scale-75 origin-right absolute top-4 right-4"
                    data-tina-field={tinaField(tinaFieldData, "trialLabel")}
                  >
                    {trialLabel}
                  </Badge>
                )}
                <h2 className="h3 text-title" data-tina-field={tinaField(tinaFieldData, `pricingPlans.${index}.title`)}>
                  {plan.title}
                </h2>
                <p data-tina-field={tinaField(tinaFieldData, `pricingPlans.${index}.subtitle`)}>{plan.subtitle}</p>

                <div className="flex-1 flex flex-col">
                  <div className="flex items-end gap-2 mt-4 mb-5">
                    <p className="h2 text-title" data-tina-field={tinaField(tinaFieldData, `pricingPlans.${index}.${billingPeriod}Pricing.price`)}>
                      {pricing?.price}
                    </p>
                    <p className="text-lg" data-tina-field={tinaField(tinaFieldData, `pricingPlans.${index}.${billingPeriod}Pricing.priceSuffix`)}>
                      {pricing?.priceSuffix}
                    </p>
                  </div>
                  <div className="flex flex-col gap-3">
                    {plan.planItems?.map((item, itemIndex) => (
                      <div key={textKey(item, itemIndex)} className="flex items-baseline gap-3">
                        <div className="w-4.5 h-4.5 bg-primary-500 rounded-full flex items-center justify-center shrink-0">
                          <Check className="size-3 text-white" />
                        </div>
                        <p className="text-title" data-tina-field={tinaField(tinaFieldData, `pricingPlans.${index}.planItems.${itemIndex}`)}>
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-6 flex items-end" data-tina-field={tinaField(tinaFieldData, `pricingPlans.${index}.button.label`)}>
                  <Button size="lg" className="w-full" variant={index === pricingPlans.length - 1 ? "outline" : "default"} asChild>
                    <Link href={plan.button?.link || "/book-demo"}>{plan.button?.label || "Book Demo"}</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </Section>
  );
}
